import React, { useMemo, useState } from "react";
import styled, { keyframes } from "styled-components";
import { toast } from "react-toastify";
import {
  FiX,
  FiSave,
  FiChevronRight,
  FiInfo,
} from "react-icons/fi";
import { GoCheck } from "react-icons/go";
import { tokens, surface, hairline } from "../../../styles/designTokens";
import { eyebrow, mono } from "../styles";
import TemplateDropzone from "./TemplateDropzone";
import TemplatePreviewFrame from "./TemplatePreviewFrame";

const overlayFade = keyframes`
  from { opacity: 0; }
  to   { opacity: 1; }
`;

const modalIn = keyframes`
  from { opacity: 0; transform: translateY(12px) scale(0.98); }
  to   { opacity: 1; transform: translateY(0) scale(1); }
`;

const ModalOverlay = styled.div`
  position: fixed;
  inset: 0;
  background: rgba(0, 0, 0, 0.82);
  backdrop-filter: blur(10px) saturate(140%);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 9999;
  padding: 2em;
  animation: ${overlayFade} ${tokens.durBase} ${tokens.easeOut};
`;

const ModalContainer = styled.div`
  background: ${({ theme }) => surface(theme, 0)};
  border: 1px solid ${({ theme }) => hairline(theme)};
  border-radius: ${tokens.radiusXl};
  width: 100%;
  max-width: 620px;
  height: min(760px, 92vh);
  display: flex;
  flex-direction: column;
  overflow: hidden;
  box-shadow: 0 24px 60px rgba(0, 0, 0, 0.45);
  animation: ${modalIn} ${tokens.durBase} ${tokens.easeOut};
`;

const ModalHeader = styled.div`
  padding: 18px 22px 14px;
  border-bottom: 1px solid ${({ theme }) => hairline(theme)};
  background: ${({ theme }) => surface(theme, 0)};
  flex-shrink: 0;

  .header-top {
    display: flex;
    align-items: flex-start;
    justify-content: space-between;
    gap: 14px;
    margin-bottom: 16px;

    .title-block {
      display: flex;
      flex-direction: column;
      gap: 4px;
      min-width: 0;
      flex: 1;

      .eyebrow { ${eyebrow}; font-size: 0.58em; opacity: 0.4; }

      h3 {
        margin: 2px 0 0 0;
        font-size: 1.1em;
        font-weight: 600;
        color: ${({ theme }) => theme.text};
        letter-spacing: -0.015em;
      }
    }

    .close-btn {
      width: 32px;
      height: 32px;
      display: inline-flex;
      align-items: center;
      justify-content: center;
      background: ${({ theme }) => surface(theme, 1)};
      border: 1px solid ${({ theme }) => hairline(theme)};
      border-radius: ${tokens.radiusMd};
      color: ${({ theme }) => theme.text};
      opacity: 0.65;
      cursor: pointer;
      transition: all ${tokens.durBase} ${tokens.easeOut};
      flex-shrink: 0;

      svg { font-size: 1em; }

      &:hover:not(:disabled) { opacity: 1; border-color: ${({ theme }) => hairline(theme, true)}; }
      &:disabled { opacity: 0.25; cursor: not-allowed; }
    }
  }

  .stepper {
    display: flex;
    align-items: center;
    gap: 8px;
    padding: 4px 4px 2px;

    .step-item {
      display: flex;
      flex-direction: column;
      align-items: center;
      gap: 6px;
      flex-shrink: 0;
      transition: all ${tokens.durBase} ${tokens.easeOut};

      .step-dot {
        width: 28px;
        height: 28px;
        display: inline-flex;
        align-items: center;
        justify-content: center;
        background: ${({ theme }) => surface(theme, 1)};
        border: 1px solid ${({ theme }) => hairline(theme)};
        border-radius: 50%;
        color: ${({ theme }) => theme.text};
        opacity: 0.4;
        transition: all ${tokens.durBase} ${tokens.easeOut};

        svg { font-size: 0.85em; }
      }

      .step-label {
        ${mono};
        font-size: 0.58em;
        font-weight: 500;
        text-transform: uppercase;
        letter-spacing: 0.08em;
        color: ${({ theme }) => theme.text};
        opacity: 0.4;
        transition: opacity ${tokens.durBase} ${tokens.easeOut};
      }

      &.active {
        .step-dot {
          background: ${tokens.brandSoft};
          border-color: ${tokens.brand};
          color: ${tokens.brand};
          opacity: 1;
          box-shadow: 0 0 0 3px ${tokens.brandRing};
        }
        .step-label { color: ${tokens.brand}; opacity: 1; }
      }

      &.done {
        .step-dot { background: ${tokens.brand}; border-color: ${tokens.brand}; color: white; opacity: 1; }
        .step-label { opacity: 0.7; }
      }
    }

    .step-line {
      flex: 1;
      height: 1px;
      background: ${({ theme }) => hairline(theme)};
      transition: background ${tokens.durBase} ${tokens.easeOut};
      margin-top: -14px;

      &.done { background: ${tokens.brand}; opacity: 0.5; }
    }
  }
`;

const ModalBody = styled.div`
  flex: 1;
  min-height: 0;
  display: flex;
  flex-direction: column;
  overflow: hidden;
`;

const ScrollArea = styled.div`
  flex: 1;
  min-height: 0;
  overflow-y: auto;
  overflow-x: hidden;
  padding: 18px 22px 14px;
  display: flex;
  flex-direction: column;
  gap: 14px;

  &::-webkit-scrollbar { width: 6px; }
  &::-webkit-scrollbar-track { background: transparent; }
  &::-webkit-scrollbar-thumb { background: ${({ theme }) => hairline(theme, true)}; border-radius: 3px; }
`;

const StepFooter = styled.div`
  flex-shrink: 0;
  padding: 14px 22px 16px;
  border-top: 1px solid ${({ theme }) => hairline(theme)};
  background: ${({ theme }) => surface(theme, 0)};
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 10px;
`;

const InfoBox = styled.div`
  display: flex;
  align-items: flex-start;
  gap: 10px;
  padding: 11px 14px;
  background: ${({ theme }) => surface(theme, 1)};
  border: 1px solid ${({ theme }) => hairline(theme)};
  border-radius: ${tokens.radiusMd};

  svg { color: ${tokens.brand}; opacity: 0.85; font-size: 0.95em; flex-shrink: 0; margin-top: 2px; }

  p { margin: 0; font-size: 0.78em; line-height: 1.5; color: ${({ theme }) => theme.text}; opacity: 0.75; letter-spacing: -0.005em; }
`;

const FieldGroup = styled.div`
  display: flex;
  flex-direction: column;
  gap: 6px;

  label {
    ${mono};
    font-size: 0.62em;
    font-weight: 500;
    text-transform: uppercase;
    letter-spacing: 0.06em;
    color: ${({ theme }) => theme.text};
    opacity: 0.5;
  }

  input, select {
    width: 100%;
    box-sizing: border-box;
    padding: 10px 12px;
    background: ${({ theme }) => surface(theme, 1)};
    border: 1px solid ${({ theme }) => hairline(theme)};
    border-radius: ${tokens.radiusMd};
    color: ${({ theme }) => theme.text};
    font-family: ${tokens.fontSans};
    font-size: 0.85em;
    outline: none;
    transition: border-color ${tokens.durBase} ${tokens.easeOut}, box-shadow ${tokens.durBase} ${tokens.easeOut};

    &:focus { border-color: ${tokens.brand}; box-shadow: 0 0 0 3px ${tokens.brandRing}; }
    &::placeholder { opacity: 0.4; }
  }

  .field-row {
    display: flex;
    gap: 10px;

    > * {
      flex: 1;
      min-width: 0;
      display: flex;
      flex-direction: column;
      gap: 6px;
    }
  }
`;

const SummaryCard = styled.div`
  ${({ theme }) => `
    background: ${surface(theme, 1)};
    border: 1px solid ${hairline(theme)};
  `}
  border-radius: ${tokens.radiusMd};
  padding: 14px;
  display: flex;
  flex-direction: column;
  gap: 8px;

  .summary-row {
    display: flex;
    align-items: baseline;
    justify-content: space-between;
    gap: 10px;

    span:first-child {
      ${mono};
      font-size: 0.62em;
      text-transform: uppercase;
      letter-spacing: 0.06em;
      color: ${({ theme }) => theme.text};
      opacity: 0.5;
      white-space: nowrap;
    }

    span:last-child {
      font-size: 0.82em;
      font-weight: 500;
      color: ${({ theme }) => theme.text};
      text-align: right;
      overflow: hidden;
      text-overflow: ellipsis;
    }
  }
`;

const PrimaryButton = styled.button`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  gap: 8px;
  padding: 11px 24px;
  background: ${tokens.brand};
  color: white;
  border: 1px solid ${tokens.brand};
  border-radius: ${tokens.radiusMd};
  font-family: ${tokens.fontSans};
  font-size: 0.85em;
  font-weight: 500;
  letter-spacing: -0.005em;
  cursor: pointer;
  transition: all ${tokens.durBase} ${tokens.easeOut};
  white-space: nowrap;

  svg { font-size: 1em; }

  &:hover:not(:disabled) {
    background: ${tokens.brandHover};
    border-color: ${tokens.brandHover};
    transform: translateY(-1px);
    box-shadow: 0 4px 16px rgba(16, 185, 129, 0.25);
  }

  &:disabled { opacity: 0.4; cursor: not-allowed; }
`;

const SecondaryButton = styled.button`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  gap: 8px;
  padding: 11px 20px;
  background: transparent;
  color: ${({ theme }) => theme.text};
  border: 1px solid ${({ theme }) => hairline(theme)};
  border-radius: ${tokens.radiusMd};
  font-family: ${tokens.fontSans};
  font-size: 0.85em;
  font-weight: 500;
  cursor: pointer;
  transition: all ${tokens.durBase} ${tokens.easeOut};
  white-space: nowrap;

  &:hover:not(:disabled) { border-color: ${({ theme }) => hairline(theme, true)}; background: ${({ theme }) => surface(theme, 1)}; }
  &:disabled { opacity: 0.4; cursor: not-allowed; }
`;

const STEPS = [
  { key: "info", label: "Informações" },
  { key: "template", label: "Template" },
  { key: "review", label: "Revisão" },
];

const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

// Mesmo limite aplicado no backend (EmailMarketingController.sendTestCampaign).
const MAX_TEST_LIST_SIZE = 25;

export default function NewCampaignModal({
  isOpen,
  onClose,
  lists,
  getListContactsCount,
  onCreateDraft,
}) {
  const [step, setStep] = useState(0);
  const [name, setName] = useState("");
  const [subject, setSubject] = useState("");
  const [senderName, setSenderName] = useState("FH Souza");
  const [senderEmail, setSenderEmail] = useState("");
  const [listId, setListId] = useState(lists[0]?.id || "");
  const [testListId, setTestListId] = useState(lists[0]?.id || "");
  const [template, setTemplate] = useState(null); // { fileName, size, htmlContent }
  const [savingDraft, setSavingDraft] = useState(false);

  const recipientsCount = useMemo(
    () => (listId ? getListContactsCount(listId) : 0),
    [listId, getListContactsCount]
  );
  const testRecipientsCount = useMemo(
    () => (testListId ? getListContactsCount(testListId) : 0),
    [testListId, getListContactsCount]
  );

  const resetAndClose = () => {
    setStep(0);
    setName("");
    setSubject("");
    setSenderName("FH Souza");
    setSenderEmail("");
    setListId(lists[0]?.id || "");
    setTestListId(lists[0]?.id || "");
    setTemplate(null);
    setSavingDraft(false);
    onClose();
  };

  if (!isOpen) return null;

  const step1Valid =
    name.trim() && subject.trim() && senderEmail.trim() && emailRegex.test(senderEmail) && listId && testListId;
  const step2Valid = !!template;

  const buildCampaignData = () => ({
    name: name.trim(),
    subject: subject.trim(),
    senderName: senderName.trim() || "FH Souza",
    senderEmail: senderEmail.trim(),
    listId,
    testListId,
    htmlContent: template?.htmlContent || "",
    fileName: template?.fileName || "",
  });

  // Criar uma campanha NUNCA dispara envio automático — ela é sempre salva
  // como rascunho. Enviar teste, disparar de verdade ou agendar são ações
  // separadas, feitas depois no card/modal de gerenciamento da campanha.
  const handleSaveDraft = async () => {
    setSavingDraft(true);
    try {
      await onCreateDraft(buildCampaignData());
      toast.success('Campanha salva como rascunho. Abra o card dela para enviar um teste, disparar ou agendar.');
      resetAndClose();
    } catch (error) {
      toast.error(error?.response?.data?.message || "Erro ao salvar campanha");
    } finally {
      setSavingDraft(false);
    }
  };

  const listName = lists.find((l) => l.id === listId)?.name || "—";
  const testListName = lists.find((l) => l.id === testListId)?.name || "—";

  return (
    <>
      <ModalOverlay onClick={resetAndClose}>
        <ModalContainer onClick={(e) => e.stopPropagation()}>
          <ModalHeader>
            <div className="header-top">
              <div className="title-block">
                <span className="eyebrow">EMAIL MARKETING</span>
                <h3>Nova Campanha</h3>
              </div>
              <button className="close-btn" onClick={resetAndClose}>
                <FiX />
              </button>
            </div>

            <div className="stepper">
              {STEPS.map((s, i) => (
                <React.Fragment key={s.key}>
                  <div className={`step-item ${i === step ? "active" : ""} ${i < step ? "done" : ""}`}>
                    <div className="step-dot">{i < step ? <GoCheck /> : i + 1}</div>
                    <span className="step-label">{s.label}</span>
                  </div>
                  {i < STEPS.length - 1 && (
                    <div className={`step-line ${i < step ? "done" : ""}`} />
                  )}
                </React.Fragment>
              ))}
            </div>
          </ModalHeader>

          <ModalBody>
            {step === 0 && (
              <ScrollArea>
                <InfoBox>
                  <FiInfo />
                  <p>Defina o nome interno da campanha, o assunto do email e para qual lista de contatos ela será enviada.</p>
                </InfoBox>

                <FieldGroup>
                  <label>Nome da campanha</label>
                  <input
                    type="text"
                    placeholder="Ex: Promoção de Aniversário"
                    value={name}
                    onChange={(e) => setName(e.target.value)}
                  />
                </FieldGroup>

                <FieldGroup>
                  <label>Assunto do email</label>
                  <input
                    type="text"
                    placeholder="Ex: Um presente especial para você 🎉"
                    value={subject}
                    onChange={(e) => setSubject(e.target.value)}
                  />
                </FieldGroup>

                <FieldGroup>
                  <div className="field-row">
                    <div>
                      <label>Nome do remetente</label>
                      <input
                        type="text"
                        placeholder="Ex: FH Souza"
                        value={senderName}
                        onChange={(e) => setSenderName(e.target.value)}
                      />
                    </div>
                    <div>
                      <label>Email do remetente</label>
                      <input
                        type="email"
                        placeholder="contato@fhsouza.com.br"
                        value={senderEmail}
                        onChange={(e) => setSenderEmail(e.target.value)}
                      />
                    </div>
                  </div>
                </FieldGroup>

                <FieldGroup>
                  <label>Lista de destinatários (real)</label>
                  <select value={listId} onChange={(e) => setListId(e.target.value)}>
                    {lists.length === 0 && <option value="">Nenhuma lista cadastrada</option>}
                    {lists.map((l) => (
                      <option key={l.id} value={l.id}>
                        {l.name} ({getListContactsCount(l.id)} contatos)
                      </option>
                    ))}
                  </select>
                </FieldGroup>

                <FieldGroup>
                  <label>Lista de teste</label>
                  <select value={testListId} onChange={(e) => setTestListId(e.target.value)}>
                    {lists.length === 0 && <option value="">Nenhuma lista cadastrada</option>}
                    {lists.map((l) => (
                      <option key={l.id} value={l.id}>
                        {l.name} ({getListContactsCount(l.id)} contatos)
                      </option>
                    ))}
                  </select>
                </FieldGroup>

                {testRecipientsCount > MAX_TEST_LIST_SIZE && (
                  <InfoBox style={{ borderColor: "rgba(239, 68, 68, 0.35)", background: "rgba(239, 68, 68, 0.08)" }}>
                    <FiInfo style={{ color: tokens.danger }} />
                    <p>
                      Essa lista tem {testRecipientsCount} contatos — parece ser uma lista real, não uma lista de
                      teste. Use uma lista pequena (só com seus emails de validação) como lista de teste.
                    </p>
                  </InfoBox>
                )}
              </ScrollArea>
            )}

            {step === 1 && (
              <ScrollArea>
                <InfoBox>
                  <FiInfo />
                  <p>Faça upload do template HTML da campanha. Você poderá visualizar o preview em Desktop e Mobile antes de disparar.</p>
                </InfoBox>

                <TemplateDropzone
                  file={template}
                  onFileAccepted={(f) => setTemplate(f)}
                />

                {template && (
                  <TemplatePreviewFrame htmlContent={template.htmlContent} height={280} />
                )}
              </ScrollArea>
            )}

            {step === 2 && (
              <ScrollArea>
                <InfoBox>
                  <FiInfo />
                  <p>
                    Revise as informações e salve a campanha. Ela fica como <strong>rascunho</strong> — nada é
                    enviado agora. Depois de salva, abra o card dela para enviar um teste, disparar de verdade ou
                    agendar o envio.
                  </p>
                </InfoBox>

                <SummaryCard>
                  <div className="summary-row"><span>Nome</span><span>{name}</span></div>
                  <div className="summary-row"><span>Assunto</span><span>{subject}</span></div>
                  <div className="summary-row"><span>Remetente</span><span>{senderName} &lt;{senderEmail}&gt;</span></div>
                  <div className="summary-row"><span>Lista real</span><span>{listName} ({recipientsCount})</span></div>
                  <div className="summary-row"><span>Lista de teste</span><span>{testListName} ({testRecipientsCount})</span></div>
                  <div className="summary-row"><span>Template</span><span>{template?.fileName}</span></div>
                </SummaryCard>

                <TemplatePreviewFrame htmlContent={template?.htmlContent} height={220} />
              </ScrollArea>
            )}
          </ModalBody>

          <StepFooter>
            <div>
              {step > 0 && (
                <SecondaryButton onClick={() => setStep((s) => s - 1)} disabled={savingDraft}>
                  Voltar
                </SecondaryButton>
              )}
            </div>

            {step < 2 && (
              <PrimaryButton
                onClick={() => setStep((s) => s + 1)}
                disabled={(step === 0 && !step1Valid) || (step === 1 && !step2Valid)}
              >
                Avançar <FiChevronRight />
              </PrimaryButton>
            )}

            {step === 2 && (
              <PrimaryButton onClick={handleSaveDraft} disabled={savingDraft}>
                <FiSave /> {savingDraft ? "Salvando..." : "Salvar Campanha"}
              </PrimaryButton>
            )}
          </StepFooter>
        </ModalContainer>
      </ModalOverlay>
    </>
  );
}
